import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { discountCodesAPI, visaAPI } from '../services/api';
import Sidebar from '../components/Sidebar';
import './DiscountCodeForm.css';

const DiscountCodeForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [visaConfigs, setVisaConfigs] = useState([]);
  const [visaCategories, setVisaCategories] = useState([]);
  const [countries, setCountries] = useState([]);

  const [formData, setFormData] = useState({
    code: '',
    description: '',
    type: 'fixed',
    value: '',
    max_discount: '',
    min_order_amount: '0',
    scope: 'universal',
    visa_config_ids: [],
    country_ids: [],
    per_user_limit: '1',
    total_usage_limit: '',
    is_active: true,
    starts_at: '',
    expires_at: ''
  });

  useEffect(() => {
    fetchVisaConfigs();
    fetchVisaCategories();
    fetchCountries();
    if (isEditMode) fetchDiscountCode();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchVisaConfigs = async () => {
    try {
      const response = await visaAPI.getConfigs({ limit: 200 });
      if (response.data.success) {
        setVisaConfigs(response.data.data.configs || response.data.data.visaConfigs || []);
      }
    } catch (error) {
      console.error('Failed to fetch visa configs:', error);
    }
  };

  const fetchVisaCategories = async () => {
    try {
      const response = await visaAPI.getCategories();
      if (response.data.success) {
        setVisaCategories(response.data.data.categories || response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch visa categories:', error);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await visaAPI.getCountries();
      if (response.data.success) {
        setCountries(response.data.data.countries || response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch countries:', error);
    }
  };

  const fetchDiscountCode = async () => {
    try {
      setLoading(true);
      const response = await discountCodesAPI.getById(id);
      if (response.data.success) {
        const code = response.data.data.discountCode;
        setFormData({
          code: code.code || '',
          description: code.description || '',
          type: code.type || 'fixed',
          value: code.value?.toString() || '',
          max_discount: code.max_discount?.toString() || '',
          min_order_amount: code.min_order_amount?.toString() || '0',
          scope: code.scope || 'universal',
          visa_config_ids: code.visa_config_ids || [],
          country_ids: code.country_ids || [],
          per_user_limit: code.per_user_limit?.toString() || '1',
          total_usage_limit: code.total_usage_limit?.toString() || '',
          is_active: Boolean(code.is_active),
          starts_at: code.starts_at ? code.starts_at.slice(0, 16) : '',
          expires_at: code.expires_at ? code.expires_at.slice(0, 16) : ''
        });
      }
    } catch (error) {
      console.error('Failed to fetch discount code:', error);
      setError('Failed to load discount code');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError('');
  };

  // Derive only categories and countries that have at least one visa configured
  const configuredCategoryIds = new Set(visaConfigs.map(vc => vc.category_id));
  const configuredCategories = visaCategories.filter(cat => configuredCategoryIds.has(cat.id));
  const configuredCountryIds = new Set(visaConfigs.map(vc => vc.country_id));
  const configuredCountries = countries.filter(c => configuredCountryIds.has(c.id));

  const generateCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let code = '';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    handleChange('code', code);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.code.trim()) {
      setError('Code is required');
      return;
    }
    if (!formData.value || parseFloat(formData.value) <= 0) {
      setError('Value must be a positive number');
      return;
    }
    if (formData.type === 'capped_percentage' && (!formData.max_discount || parseFloat(formData.max_discount) <= 0)) {
      setError('Max discount is required for capped percentage type');
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        ...formData,
        code: formData.code.trim().toUpperCase(),
        value: parseFloat(formData.value),
        max_discount: formData.max_discount ? parseFloat(formData.max_discount) : null,
        min_order_amount: parseFloat(formData.min_order_amount) || 0,
        per_user_limit: parseInt(formData.per_user_limit) || 1,
        total_usage_limit: formData.total_usage_limit ? parseInt(formData.total_usage_limit) : null,
        visa_config_ids: formData.scope === 'specific' ? formData.visa_config_ids : null,
        country_ids: formData.scope === 'specific' ? formData.country_ids : null,
        starts_at: formData.starts_at || null,
        expires_at: formData.expires_at || null
      };

      if (isEditMode) {
        await discountCodesAPI.update(id, payload);
      } else {
        await discountCodesAPI.create(payload);
      }
      navigate('/discount-codes');
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to save discount code');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-layout">
        <Sidebar />
        <main className="dashboard-main">
          <div className="loading-state"><div className="spinner"></div><p>Loading...</p></div>
        </main>
      </div>
    );
  }

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="dashboard-main">
        <div className="dashboard-header">
          <div className="header-left">
            <h1 className="page-title">{isEditMode ? 'EDIT DISCOUNT CODE' : 'CREATE DISCOUNT CODE'}</h1>
            <p className="page-subtitle">
              <button className="btn-back" onClick={() => navigate('/discount-codes')}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 12H5"></path>
                  <polyline points="12 19 5 12 12 5"></polyline>
                </svg>
                BACK TO DISCOUNT CODES
              </button>
            </p>
          </div>
        </div>

        <div className="dashboard-content">
          <form className="dc-form" onSubmit={handleSubmit}>
            {error && <div className="form-error">{error}</div>}

            {/* Basic Info */}
            <div className="form-section">
              <h3 className="form-section-title">BASIC INFORMATION</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">CODE *</label>
                  <div className="code-input-group">
                    <input
                      type="text"
                      className="form-input code-input"
                      value={formData.code}
                      onChange={(e) => handleChange('code', e.target.value.toUpperCase())}
                      placeholder="e.g. SUMMER20"
                      disabled={isEditMode}
                      maxLength={50}
                    />
                    {!isEditMode && (
                      <button type="button" className="btn-generate" onClick={generateCode}>
                        GENERATE
                      </button>
                    )}
                  </div>
                  {isEditMode && <span className="form-hint">Code cannot be changed after creation</span>}
                </div>
                <div className="form-group">
                  <label className="form-label">DESCRIPTION</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    placeholder="e.g. Summer sale - 20% off"
                    maxLength={255}
                  />
                </div>
              </div>
            </div>

            {/* Discount Type & Value */}
            <div className="form-section">
              <h3 className="form-section-title">DISCOUNT SETTINGS</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">TYPE *</label>
                  <div className="toggle-group">
                    <button
                      type="button"
                      className={`toggle-btn ${formData.type === 'fixed' ? 'active' : ''}`}
                      onClick={() => handleChange('type', 'fixed')}
                    >
                      Fixed $
                    </button>
                    <button
                      type="button"
                      className={`toggle-btn ${formData.type === 'percentage' ? 'active' : ''}`}
                      onClick={() => handleChange('type', 'percentage')}
                    >
                      Percent %
                    </button>
                    <button
                      type="button"
                      className={`toggle-btn ${formData.type === 'capped_percentage' ? 'active' : ''}`}
                      onClick={() => handleChange('type', 'capped_percentage')}
                    >
                      Capped %
                    </button>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    VALUE * {formData.type === 'fixed' ? '($)' : '(%)'}
                  </label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.value}
                    onChange={(e) => handleChange('value', e.target.value)}
                    placeholder={formData.type === 'fixed' ? '10.00' : '20'}
                    step={formData.type === 'fixed' ? '0.01' : '1'}
                    min="0"
                    max={formData.type !== 'fixed' ? '100' : undefined}
                  />
                </div>
                {formData.type === 'capped_percentage' && (
                  <div className="form-group">
                    <label className="form-label">MAX DISCOUNT ($) *</label>
                    <input
                      type="number"
                      className="form-input"
                      value={formData.max_discount}
                      onChange={(e) => handleChange('max_discount', e.target.value)}
                      placeholder="50.00"
                      step="0.01"
                      min="0"
                    />
                    <span className="form-hint">Maximum discount amount cap</span>
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">MIN ORDER AMOUNT ($)</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.min_order_amount}
                    onChange={(e) => handleChange('min_order_amount', e.target.value)}
                    placeholder="0"
                    step="0.01"
                    min="0"
                  />
                  <span className="form-hint">Minimum order total to apply code (0 = no minimum)</span>
                </div>
              </div>
            </div>

            {/* Scope & Restrictions */}
            <div className="form-section">
              <h3 className="form-section-title">SCOPE & RESTRICTIONS</h3>
              <div className="form-group">
                <label className="form-label">SCOPE</label>
                <div className="toggle-group">
                  <button
                    type="button"
                    className={`toggle-btn ${formData.scope === 'universal' ? 'active' : ''}`}
                    onClick={() => handleChange('scope', 'universal')}
                  >
                    Universal
                  </button>
                  <button
                    type="button"
                    className={`toggle-btn ${formData.scope === 'specific' ? 'active' : ''}`}
                    onClick={() => handleChange('scope', 'specific')}
                  >
                    Specific
                  </button>
                </div>
                <span className="form-hint">
                  Universal = applies to all visas. Specific = only selected visa types/countries.
                </span>
              </div>

              {formData.scope === 'specific' && (
                <>
                  <div className="form-group" style={{ marginTop: '16px' }}>
                    <label className="form-label">VISA TYPES</label>
                    <MultiSelectDropdown
                      options={configuredCategories.map(cat => ({ id: cat.id, label: cat.name }))}
                      selected={formData.visa_config_ids || []}
                      onChange={(ids) => handleChange('visa_config_ids', ids)}
                      placeholder="Search visa types..."
                      emptyText="No configured visa types found"
                    />
                  </div>
                  <div className="form-group" style={{ marginTop: '16px' }}>
                    <label className="form-label">COUNTRIES</label>
                    <MultiSelectDropdown
                      options={configuredCountries.map(c => ({ id: c.id, label: c.name }))}
                      selected={formData.country_ids || []}
                      onChange={(ids) => handleChange('country_ids', ids)}
                      placeholder="Search countries..."
                      emptyText="No configured countries found"
                    />
                  </div>
                </>
              )}
            </div>

            {/* Usage Limits */}
            <div className="form-section">
              <h3 className="form-section-title">USAGE LIMITS</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">PER USER LIMIT</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.per_user_limit}
                    onChange={(e) => handleChange('per_user_limit', e.target.value)}
                    placeholder="1"
                    min="0"
                  />
                  <span className="form-hint">0 = unlimited uses per user</span>
                </div>
                <div className="form-group">
                  <label className="form-label">TOTAL USAGE LIMIT</label>
                  <input
                    type="number"
                    className="form-input"
                    value={formData.total_usage_limit}
                    onChange={(e) => handleChange('total_usage_limit', e.target.value)}
                    placeholder="Empty = unlimited"
                    min="0"
                  />
                  <span className="form-hint">Leave empty for unlimited total uses</span>
                </div>
              </div>
            </div>

            {/* Schedule */}
            <div className="form-section">
              <h3 className="form-section-title">SCHEDULE</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">START DATE</label>
                  <input
                    type="datetime-local"
                    className="form-input"
                    value={formData.starts_at}
                    onChange={(e) => handleChange('starts_at', e.target.value)}
                  />
                  <span className="form-hint">Leave empty for immediate activation</span>
                </div>
                <div className="form-group">
                  <label className="form-label">EXPIRY DATE</label>
                  <input
                    type="datetime-local"
                    className="form-input"
                    value={formData.expires_at}
                    onChange={(e) => handleChange('expires_at', e.target.value)}
                  />
                  <span className="form-hint">Leave empty for no expiration</span>
                </div>
              </div>
            </div>

            {/* Status */}
            <div className="form-section">
              <h3 className="form-section-title">STATUS</h3>
              <div className="form-group">
                <div className="toggle-group">
                  <button
                    type="button"
                    className={`toggle-btn ${formData.is_active ? 'active' : ''}`}
                    onClick={() => handleChange('is_active', true)}
                  >
                    Active
                  </button>
                  <button
                    type="button"
                    className={`toggle-btn ${!formData.is_active ? 'active inactive' : ''}`}
                    onClick={() => handleChange('is_active', false)}
                  >
                    Inactive
                  </button>
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="form-actions">
              <button type="button" className="btn-modal-close" onClick={() => navigate('/discount-codes')}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? 'Saving...' : (isEditMode ? 'UPDATE CODE' : 'CREATE CODE')}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

// Multi-select dropdown component
const MultiSelectDropdown = ({ options, selected, onChange, placeholder, emptyText }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = options.filter(o =>
    o.label.toLowerCase().includes(search.toLowerCase())
  );

  const toggle = (id) => {
    if (selected.includes(id)) {
      onChange(selected.filter(v => v !== id));
    } else {
      onChange([...selected, id]);
    }
  };

  const selectedLabels = options.filter(o => selected.includes(o.id));

  return (
    <div className="ms-dropdown" ref={ref}>
      <div className="ms-dropdown-trigger" onClick={() => setIsOpen(!isOpen)}>
        {selectedLabels.length === 0 ? (
          <span className="ms-placeholder">{placeholder || 'Select...'}</span>
        ) : (
          <div className="ms-tags">
            {selectedLabels.map(o => (
              <span key={o.id} className="ms-tag">
                {o.label}
                <button type="button" className="ms-tag-remove" onClick={(e) => { e.stopPropagation(); toggle(o.id); }}>&times;</button>
              </span>
            ))}
          </div>
        )}
        <svg className={`ms-chevron ${isOpen ? 'open' : ''}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9"></polyline></svg>
      </div>
      {isOpen && (
        <div className="ms-dropdown-menu">
          <input
            type="text"
            className="ms-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={placeholder}
            autoFocus
          />
          <div className="ms-options">
            {filtered.length === 0 ? (
              <div className="ms-empty">{emptyText || 'No options'}</div>
            ) : (
              filtered.map(o => (
                <label key={o.id} className="ms-option">
                  <input
                    type="checkbox"
                    checked={selected.includes(o.id)}
                    onChange={() => toggle(o.id)}
                  />
                  <span>{o.label}</span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DiscountCodeForm;
